import { useEffect, useRef } from "react";

import isEmpty from "lodash/isEmpty";

import { router, usePathname } from "@/crossPlatform/expo-router";
import useEncodedSearchParams from "@/hooks/useEncodedSearchParams";
import { useAnalytics } from "@/services/Analytics";
import { isWeb } from "@/utils/platform.util";

interface UseSubDeepLinkConfig {
  /** Analytics event name to track when deep link fires */
  analyticsEvent: string;
  /** Analytics properties to include with the event. The matched param's value is automatically added under its param name. */
  analyticsProps: Record<string, string>;
  /** Other nav params used by the deep link that can uniquely identify it (used on web for deep links where 'target' isnt provided, but on mobile it is implied/inserted) */
  deeplinkIdentifiers?: string[];
  /** Callback to execute when the target matches */
  onOpen: () => void;
  /**
   * Which query param to match against. Defaults to `"target"` (the deep-link
   * target). Use `"subtarget"` for a nested, in-app-only sub-target handled by a
   * screen that a `target` deep link already routed to.
   */
  paramName?: "subtarget" | "target";
  /** Optional permission check - if false, the deep link won't fire */
  shouldHandle?: boolean;
  /** The target query param value to match (e.g., "add", "search-groups") */
  targetValue: string;
}

/**
 * Generic hook for handling sub-deep links within a route.
 *
 * Listens for the configured query parameter (`target` by default, or
 * `subtarget`) and triggers the onOpen callback when it matches the specified
 * targetValue. Automatically tracks analytics with the matched param's value
 * added to analyticsProps under the param's name.
 * Supports repeated opens via the `deepLinkReceivedAt` timestamp parameter.
 *
 * Uses ref pattern to capture latest values at fire time without re-triggering the effect.
 *
 * @example
 * ```tsx
 * useSubDeepLink({
 *   targetValue: SMTModalSubDeepLinks.Add,
 *   onOpen: handleAddEntry,
 *   shouldHandle: shouldShowAddEntry,
 *   analyticsEvent: SMTEntryAnalyticsEvent.AddEntryOpenedFromDeepLink,
 *   analyticsProps: {
 *     component: AnalyticsComponent.FloatingButton,
 *     source: SMTAnalyticsSource.ViewAllSMTDailyEntriesModal,
 *   },
 * });
 * ```
 */
const useSubDeepLink = ({
  analyticsEvent,
  analyticsProps,
  deeplinkIdentifiers = [],
  onOpen,
  paramName = "target",
  shouldHandle = true,
  targetValue,
}: UseSubDeepLinkConfig) => {
  const { track } = useAnalytics();
  // on web, snapshot the hook's own route, to pass as the path key for clearing session storage without racing window.location
  const routePathname = usePathname();
  const allParams = useEncodedSearchParams<Record<string, string>>();
  const { deepLinkReceivedAt, subtarget, target } = allParams;
  const paramValue = paramName === "subtarget" ? subtarget : target;

  const populatedIdentifiers = deeplinkIdentifiers.filter(
    (param) => !isEmpty(allParams[param]),
  );
  const isMatch = paramValue === targetValue || !isEmpty(populatedIdentifiers);

  const onOpenRef = useRef(onOpen);
  useEffect(() => {
    onOpenRef.current = onOpen;
  }, [onOpen]);

  const trackRef = useRef(track);
  useEffect(() => {
    trackRef.current = track;
  }, [track]);

  const analyticsEventRef = useRef(analyticsEvent);
  useEffect(() => {
    analyticsEventRef.current = analyticsEvent;
  }, [analyticsEvent]);

  const analyticsPropsRef = useRef(analyticsProps);
  useEffect(() => {
    analyticsPropsRef.current = analyticsProps;
  }, [analyticsProps]);

  const routePathnameRef = useRef(routePathname);
  useEffect(() => {
    routePathnameRef.current = routePathname;
  }, [routePathname]);

  const populatedIdentifiersRef = useRef(populatedIdentifiers);
  populatedIdentifiersRef.current = populatedIdentifiers;

  useEffect(() => {
    if (!shouldHandle || !isMatch) return;

    trackRef.current(analyticsEventRef.current, {
      ...analyticsPropsRef.current,
      [paramName]: targetValue,
    });

    // on web, clear identifying params from session storage before navigating away, to prevent getting trapped on dismiss -> reopen subdeeplink
    if (isWeb()) {
      const clearedParams = Object.fromEntries(
        [paramName, ...populatedIdentifiersRef.current].map((param) => [
          param,
          undefined,
        ]),
      );
      router.setParams(clearedParams, routePathnameRef.current);
    }
    onOpenRef.current();
  }, [deepLinkReceivedAt, isMatch, paramName, shouldHandle, targetValue]);
};

export default useSubDeepLink;
